import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  Stack,
  Paper,
  IconButton,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Project import
import { gridSpacing } from 'config.js';

// Styled Components
const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(color || theme.palette.primary.main, 0.08)} 0%, ${alpha(color || theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(color || theme.palette.primary.main, 0.3)
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
  }
}));

const ViewAbsensi = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [absensiData, setAbsensiData] = useState(null);

  // Static data - in real app, fetch from API
  const mockData = {
    id: parseInt(id) || 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      department: 'IT & Web Master',
      employeeId: 'EMP001',
      email: 'ahmad.rizki@eurekagroup.id',
      phone: '+62 812-3456-7890'
    },
    date: '2024-01-15',
    checkIn: '08:00',
    checkOut: '17:30',
    status: 'Hadir',
    totalHours: '9h 30m',
    location: 'Office Jakarta',
    lateMinutes: 0,
    overtimeHours: 0.5,
    notes: 'Kehadiran tepat waktu, produktif sepanjang hari',
    monthStats: {
      totalDays: 22,
      presentDays: 20,
      absentDays: 1,
      leaveDays: 1,
      lateCount: 2,
      attendanceRate: 90.9
    }
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAbsensiData(mockData);
      setLoading(false);
    }, 500);
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'hadir':
        return theme.palette.success.main;
      case 'izin':
        return theme.palette.warning.main;
      case 'cuti':
        return theme.palette.info.main;
      case 'terlambat':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!absensiData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Data absensi tidak ditemukan</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardHeader
          avatar={
            <IconButton onClick={() => navigate('/absensi')} sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          }
          title={
            <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Detail Absensi
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/absensi/edit/${id}`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Print
              </Button>
            </Stack>
          }
        />
      </Card>

      <Grid container spacing={3}>
        {/* Left Column - Employee Info & Main Stats */}
        <Grid item xs={12} lg={4}>
          {/* Employee Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700
                  }}
                >
                  {absensiData.employee.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {absensiData.employee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {absensiData.employee.position}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {absensiData.employee.department}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{absensiData.employee.employeeId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    {new Date(absensiData.date).toLocaleDateString('id-ID', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocationIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{absensiData.location}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          {/* Status Card */}
          <InfoCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Status Kehadiran
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={absensiData.status}
                  sx={{
                    bgcolor: alpha(getStatusColor(absensiData.status), 0.1),
                    color: getStatusColor(absensiData.status),
                    border: `2px solid ${getStatusColor(absensiData.status)}`,
                    fontSize: '1rem',
                    fontWeight: 700,
                    height: 48,
                    px: 3,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                  icon={absensiData.status === 'Hadir' ? <CheckCircleIcon /> : <CancelIcon />}
                />
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Middle Column - Attendance Details */}
        <Grid item xs={12} lg={5}>
          {/* Time Tracking */}
          <InfoCard sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ color: 'primary.main' }} />
                  Tracking Waktu
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <StatCard color={theme.palette.primary.main}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ScheduleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Check In
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                          {absensiData.checkIn}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Grid>
                <Grid item xs={6}>
                  <StatCard color={theme.palette.success.main}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ScheduleIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Check Out
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                          {absensiData.checkOut}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Grid>
                <Grid item xs={12}>
                  <StatCard color={theme.palette.info.main}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TimerIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Total Jam Kerja
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                          {absensiData.totalHours}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>

          {/* Additional Info */}
          <InfoCard>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Informasi Tambahan
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Catatan
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {absensiData.notes}
                  </Typography>
                </Box>
                {absensiData.lateMinutes > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Keterlambatan
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: 'error.main' }}>
                      {absensiData.lateMinutes} menit
                    </Typography>
                  </Box>
                )}
                {absensiData.overtimeHours > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Lembur
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: 'success.main' }}>
                      {absensiData.overtimeHours} jam
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Right Column - Monthly Stats */}
        <Grid item xs={12} lg={3}>
          <InfoCard>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: 'primary.main' }} />
                  Statistik Bulanan
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Kehadiran
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {absensiData.monthStats.presentDays} / {absensiData.monthStats.totalDays} hari
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={absensiData.monthStats.attendanceRate}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.success.main,
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {absensiData.monthStats.attendanceRate.toFixed(1)}% kehadiran
                  </Typography>
                </Box>

                <Divider />

                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Hari Kerja</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {absensiData.monthStats.totalDays}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Hadir</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {absensiData.monthStats.presentDays}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Tidak Hadir</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                      {absensiData.monthStats.absentDays}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Cuti</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                      {absensiData.monthStats.leaveDays}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Terlambat</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {absensiData.monthStats.lateCount}x
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewAbsensi;




